import { Heading, PageHeader, Text } from "#app/components/text.tsx";
import { buttonVariants } from "#app/components/ui/button.tsx";
import { Link } from "react-router";
import { motion, Variants } from "motion/react"
import { useEffect, useState } from "react";
import { Card, CardDescription, CardHeader, CardTitle } from "#app/components/ui/card.tsx";
import image1 from "#app/assets/garage-maintenance.jpg";
import Cta from "#app/components/cta.tsx";
import { BASE_URL, COMPANY_NAME, LOCATION_CITY_STATE, SERVICE_COUNTIES, SERVICE_REGION } from "#app/utils/constants.ts";
import { getSeoMeta } from "#app/utils/seo/getSeoMeta.ts";
import { breadcrumbSchema } from "#app/utils/seo/structured-data.ts";

const MotionCard = motion.create(Card);

const cardVariants: Variants = {
	offscreen: {
	  x: -300,
	  opacity: 0
	},
	onscreen: {
	  x: 0,
	  opacity: 1,
	  transition: {
		type: "spring",
		bounce: 0.4,
		duration: 0.8,
	  }
	}
};

export const meta = () => {
    return getSeoMeta({
        title: `Careers | ${COMPANY_NAME}`,
        description: `Join our locally owned garage door team in ${LOCATION_CITY_STATE}, serving ${SERVICE_COUNTIES}. Paid training, steady local work and room to grow.`,
        url: BASE_URL + '/careers',
        jsonLd: breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Careers', path: '/careers' },
        ]) as any,
    })
}

export const loader = async () => {
    return {}
}

export default function CareersPage() {
    return (
        <main>
            <PageHeader variant="servicesPage" heading={`Build Your Career At ${COMPANY_NAME}`} className="text-center bg-primary text-primary-foreground mb-24 sm:mb-32">
                <Text>
                Join our growing, locally owned team right here in {LOCATION_CITY_STATE}. We're dedicated to quality service, community connection, and building lasting careers across {SERVICE_COUNTIES}.
                </Text>
                <Link to="#" className={buttonVariants({ variant: "accent", size: "xl" })}>
                    Apply Now
                </Link>
            </PageHeader>
            <Features />
            <WhatItsLike />
            <TrainingAndGrowth />
            <Cta heading="Ready to Join Our Team?" backgroundStyle="accent">
                <Link to="#" className={buttonVariants({ size: "xl" })}>
                    Apply Now
                </Link>
            </Cta>
        </main>
    )
}


function Features() {
	const [isMobile, setIsMobile] = useState(true);
	useEffect(() => {
		setIsMobile(window.innerWidth < 1024);
	}, [])

	return (
		<section className="mb-24 sm:mb-32">
            <div className="mx-auto max-w-2xl text-center mb-16">
                <Heading as="h2">
                    Grow Your Career With Us in {SERVICE_REGION}
                </Heading>
            </div>
			<div className="container">
            <motion.div
					initial="offscreen"
					whileInView="onscreen"
					viewport={{ once: true, amount: isMobile ? 0.2 : 0.8 }}
					transition={{ staggerChildren: 0.2, staggerDirection: -1 }}
					className="mx-auto grid max-w-2xl auto-rows-fr grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-3"
				>
					<MotionCard variants={cardVariants} className='bg-muted text-foreground min-h-[353px]'>
						<CardHeader>
							<CardTitle className='text-center text-h3'>Work Local, <br/>Serve Your Community</CardTitle>
							<CardDescription className='text-muted-foreground text-xl text-center mx-auto pt-4'>
                                Based right here in {LOCATION_CITY_STATE}, serving {SERVICE_COUNTIES}. Spend less time with long commutes and more time providing essential services to your neighbors and local businesses.
							</CardDescription>
						</CardHeader>
					</MotionCard>
					<MotionCard variants={cardVariants} className='bg-muted text-foreground min-h-[353px]'>
						<CardHeader>
							<CardTitle className='text-center text-h3'>Supportive Team &amp;<br /> Local Roots</CardTitle>
							<CardDescription className='text-muted-foreground text-center text-xl mx-auto pt-4'>
                                Join a team built on trust, respect, and collaboration. We value every member and maintain a supportive atmosphere reflective of our locally owned origins, even as we grow.
							</CardDescription>
						</CardHeader>
					</MotionCard>
					<MotionCard variants={cardVariants} className='bg-muted text-foreground min-h-[353px]'>
						<CardHeader>
							<CardTitle className='text-center text-h3'>Training and Career Advancement</CardTitle>
							<CardDescription className='text-muted-foreground text-center text-xl mx-auto pt-4'>
                                We invest in your success! Receive comprehensive paid training on diverse residential and commercial garage door systems, operators, and the latest safety protocols to build a valuable, long-term career path.
							</CardDescription>
						</CardHeader>
					</MotionCard>
				</motion.div>
			</div>
		</section>
	)
}

function WhatItsLike() {
    return (
        <section className="mb-24 sm:mb-32">
            <div className="container">
                <div className="mx-auto grid grid-cols-1 gap-x-8 gap-y-16 sm:gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-5">
                    <div className="col-span-2">
                        <h2 className="text-base/7 tracking-wide">Our {SERVICE_REGION} Team: Culture &amp; Values</h2>
                        <p className="mt-2 text-pretty text-h2 font-serif">
                            What It's Like Working at {COMPANY_NAME}
                        </p>
                    </div>
                    <div className="col-span-3 space-y-10">
                        <p className="text-muted-foreground">
                            At {COMPANY_NAME}, we're proud of our local roots and the close-knit team environment we've built right here in {LOCATION_CITY_STATE}. Our culture is founded on <strong>teamwork, trust, and a genuine connection to the community</strong> we serve every day. We believe in empowering every team member, providing the quality tools, ongoing training, and supportive atmosphere needed to excel and take real pride in delivering top-notch garage door services.
                        </p>
                        <p className="text-muted-foreground">
                            We foster open communication and mutual respect, driven by a shared passion for quality workmanship and exceptional customer care. We're not just employees; we're neighbors serving neighbors across {SERVICE_COUNTIES}.
                        </p>
                        <dl className=" grid grid-cols-1 gap-x-8 gap-y-10 text-muted-foreground sm:grid-cols-2 lg:gap-y-16">
                            <div className="relative pl-9">
                                <dt className="font-semibold text-foreground">
                                    Community Commitment
                                </dt>
                                <dd className="mt-2">Deeply invested in {SERVICE_COUNTIES} and building lasting local relationships.</dd>
                            </div>
                            <div className="relative pl-9">
                                <dt className="font-semibold text-foreground">
                                    Integrity & Honesty
                                </dt>
                                <dd className="mt-2">Transparent communication and fair, straightforward pricing.</dd>
                            </div>
                            <div className="relative pl-9">
                                <dt className="font-semibold text-foreground">
                                    Quality Craftsmanship
                                </dt>
                                <dd className="mt-2">Taking pride in doing the job right with meticulous attention to detail.</dd>
                            </div>
                            <div className="relative pl-9">
                                <dt className="font-semibold text-foreground">
                                    Reliability & Responsiveness
                                </dt>
                                <dd className="mt-2">Providing dependable service when our customers need it most, including 24/7 emergency support.</dd>
                            </div>
                            <div className="relative pl-9">
                                <dt className="font-semibold text-foreground">
                                    Personalized Care
                                </dt>
                                <dd className="mt-2">Treating each customer's unique needs with focus and respect.</dd>
                            </div>
                            <div className="relative pl-9">
                                <dt className="font-semibold text-foreground">
                                    Safety
                                </dt>
                                <dd className="mt-2">Prioritizing the safety of our team, our customers, and their property above all else.</dd>
                            </div>
                            <div className="relative pl-9">
                                <dt className="font-semibold text-foreground">
                                    Growth & Improvement
                                </dt>
                                <dd className="mt-2">Encouraging learning and embracing new techniques and technologies to stay ahead.</dd>
                            </div>
                            <div className="relative pl-9">
                                <dt className="font-semibold text-foreground">
                                    Sustainability
                                </dt>
                                <dd className="mt-2">Opting for durable products and responsible practices for long-term value.</dd>
                            </div>
                        </dl>
                    </div>
                </div>
            </div>
        </section>
    )
}

function TrainingAndGrowth() {
    return (
        <div className="relative isolate overflow-hidden mb-24 sm:mb-32">
            <div className="container">
                <div className="grid grid-cols-1 gap-x-8 gap-y-16 sm:gap-y-20 lg:grid-cols-2 lg:items-start">
                    <div className="px-6 lg:px-0 lg:pr-4 lg:pt-4">
                        <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-lg">
                            <h2 className="mt-2 text-pretty font-semibold tracking-tight text-h2">
                                Build Your Skills & Career Path with Us
                            </h2>
                            <p className="mt-6 text-lg/8">
                                Your professional growth is important to us. When you join {COMPANY_NAME} in {LOCATION_CITY_STATE}, you get more than just a job – you get a path forward. We support our team through:
                            </p>
                            <ul className='mt-10 list-disc list-inside space-y-4'>
                                <li><strong>Paid, Hands-On Initial Training:</strong> Learn residential and commercial systems, safety procedures, and quality standards from experienced mentors.</li>
                                <li><strong>Ongoing Skill Development:</strong> Stay up-to-date with training on new products, technologies (including smart openers), and advanced repair techniques.</li>
                                <li><strong>Focus on Safety:</strong> Continuous training ensures you can perform your work safely and confidently.</li>
                                <li><strong>Advancement Potential:</strong> As a growing local business, we look to promote dedicated and skilled team members from within whenever possible.</li>
                                <li><strong>Supportive Learning Environment:</strong> Our team culture encourages asking questions and sharing knowledge to help everyone succeed.</li>
                            </ul>
                        </div>
                    </div>
                    <div className="sm:px-6 lg:px-0">
                        <div className="aspect-square sm:px-6 lg:px-0">
                            <img
                                alt="Product screenshot"
                                src={image1}
                                width={2432}
                                height={1442}
                                className="object-cover w-full h-full rounded-md"
                                loading='lazy'
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}